import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// ── In-memory access token store ────────────────────────────────────
// The access token is stored ONLY in memory — never in localStorage.
// The httpOnly refresh cookie handles session persistence across page
// reloads: on boot, the first API call triggers a 401 → the interceptor
// calls /auth/refresh (sending the httpOnly cookie) → obtains a new
// access token → retries the original request.
let _accessToken = "";

export function setAccessToken(t) { _accessToken = t || ""; }
export function getAccessToken()  { return _accessToken; }
export function clearAccessToken() {
  _accessToken = "";
}

// ── Request interceptor: attach access token ────────────────────────
api.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// ── Response interceptor: auto-refresh on 401 ───────────────────────
// When a request fails with 401 (access token expired or missing) we
// silently call /api/auth/refresh to get a new access token via the
// httpOnly refresh cookie, then replay the original request. A mutex
// prevents concurrent refresh storms.

let _refreshing = null; // Promise while a refresh is in-flight

async function refreshAccessToken() {
  try {
    const { data } = await axios.post(`${API}/auth/refresh`, null, {
      withCredentials: true,
    });
    const newToken = data.token || "";
    _accessToken = newToken;
    return newToken;
  } catch {
    // Refresh failed — force re-login
    clearAccessToken();
    return null;
  }
}

/** FastAPI returns validation errors as `detail: [{type, loc, msg, input, url}, ...]`.
 *  Any UI code that renders `err.response.data.detail` directly (toasts, JSX)
 *  would try to render objects → React crashes with "Objects are not valid as
 *  a React child". Normalise the payload here so every handler sees a plain
 *  human-readable string. */
function normaliseErrorDetail(detail) {
  if (detail == null) return detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (!d || typeof d !== "object") return String(d);
        const msg = d.msg || d.message || "";
        const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.join(" · ") || "Request failed";
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return String(detail);
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    // Only attempt refresh for 401s that aren't themselves refresh/login calls
    if (
      err?.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/login") &&
      !original.url?.includes("/auth/admin/login") &&
      !original.url?.includes("/auth/member/login")
    ) {
      original._retry = true;

      // Use a mutex so only one refresh runs at a time
      if (!_refreshing) {
        _refreshing = refreshAccessToken().finally(() => { _refreshing = null; });
      }
      const newToken = await _refreshing;

      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original); // replay the original request
      }
      // Refresh failed — let the 401 propagate
    }

    // Coerce ANY response.data.detail into a plain string so downstream
    // `toast.error(err.response.data.detail || …)` calls never receive
    // an array/object that React can't render.
    if (err?.response?.data && "detail" in err.response.data) {
      err.response.data.detail = normaliseErrorDetail(err.response.data.detail);
    }
    return Promise.reject(err);
  }
);

export default api;
